import { useEffect, useMemo, useState } from 'react';
import { Close, colors, Menu, spacing } from 'component-library';
import { useNavigate } from 'react-router';
import { useFlag } from '#lib/hooks/useFlag';
import { routePathsHome } from '../../routePaths/homeRoutePaths';
import {
  csrdRestoreLastSubpageState,
  csrdRoutePaths,
} from '../../../lib/routePaths/csrdRoutePaths';
import { co2ImpactroutePaths } from '../../../lib/routePaths/co2ImpactRoutePaths';
import { routePathsAdmin } from '../../../lib/routePaths/adminRoutePaths';
import { type BasicProfile } from '../../Profile';
import { getCo2ImpactUrl, getOnboardingUrl, getServiceUrl } from '../../utils/environment';
import {
  getUsersServices,
  userIsUserAdminForAnyPartner as _userIsAdminForAnyPartner,
} from '../../utils/services';
import { useCountries } from '../../hooks/useCountries';
import { useLearningMarkets } from '../../hooks/useLearningMarkets';
import useIsDanishIntermediary from '../../hooks/useIsDanishIntermediary';

import {
  MenuButton,
  MenuButtonWrapper,
  MobileMenuFoldout,
  MobileMenuFoldoutList,
  MobileMenuFoldoutListItem,
  MobileMenuLink,
  MobileMenuWrapper,
} from './styles';
import type { MenuService } from '../../Services';
import type { TextType } from '../types';

type MobileMenuProps = {
  profile: BasicProfile | null;
  allServices?: MenuService[];
  activeLink?: string;
  text: TextType;
  serviceNames?: Record<string, string>;
};
export const MobileMenu = ({
  profile,
  allServices = [],
  activeLink,
  serviceNames,
  text,
}: MobileMenuProps) => {
  const [showMenu, setShowMenu] = useState(false);
  const [userServices, setUserServices] = useState<MenuService[]>();
  const [userIsAdminForAnyPartner, setUserIsAdminForAnyPartner] = useState(false);

  const { supportedCountries } = useCountries();

  const activeRoute = window.location.pathname;

  const environmentFlag = useFlag('environment-service');
  const csrdFlag = useFlag('csrd');
  const csrdAllowDenmark = useFlag('csrd-allow-denmark');
  const csrdAllowFinland = useFlag('csrd-allow-finland');
  const csrdAllowPoland = useFlag('csrd-allow-poland');
  const csrdAllowSweden = useFlag('csrd-allow-sweden');
  const csrdAllowNorway = useFlag('csrd-allow-norway');
  const co2NorwayFlag = useFlag('co2-impact-norway');

  const mergeMenusFlag = useFlag('merge-cog-and-user-menu');
  const showLearning = useFlag('app-learning');
  const { hasAvailableMarket: hasLearningMarket } = useLearningMarkets();
  const isDanishIntermediary = useIsDanishIntermediary();

  const showCSRD =
    profile &&
    csrdFlag &&
    profile.aggregatedPermissions.services.followUp &&
    ((supportedCountries.sweden && csrdAllowSweden) ||
      (supportedCountries.denmark && csrdAllowDenmark) ||
      (supportedCountries.finland && csrdAllowFinland) ||
      (supportedCountries.norway && csrdAllowNorway) ||
      (supportedCountries.poland && csrdAllowPoland));

  const showEnvironment = environmentFlag && profile?.aggregatedPermissions.services.followUp;

  const showCO2Impact =
    profile?.aggregatedPermissions.services.followUp &&
    !isDanishIntermediary &&
    (supportedCountries.sweden ||
      supportedCountries.denmark ||
      (co2NorwayFlag && supportedCountries.norway));

  useEffect(() => {
    if (allServices.length && profile) {
      setUserServices(getUsersServices(profile, allServices));
      setUserIsAdminForAnyPartner(_userIsAdminForAnyPartner(profile));
    }
  }, [profile, allServices]);

  const navigate = useNavigate();

  const toggleMenu = () => {
    const body = document.querySelector('body');

    if (body) {
      body.style.overflow = showMenu ? 'auto' : 'hidden';
    }
    setShowMenu(!showMenu);
  };

  const menuLinks = useMemo(() => {
    if (!userServices || !profile) {
      return [];
    }

    const links = userServices.map((service) => ({
      linkText: serviceNames?.[service.name] ?? service.name,
      link: getServiceUrl(service),
      onLinkClick: () => {
        void navigate(getServiceUrl(service));
      },
      active: service.name === activeLink,
    }));

    if (showCO2Impact) {
      links.push({
        linkText: text.co2Impact,
        link: getCo2ImpactUrl(),
        onLinkClick: () => {
          void navigate(co2ImpactroutePaths.HOME);
        },
        active: 'co2impact' === activeLink,
      });
    }

    if (profile.aggregatedPermissions.services.followUp && showCSRD) {
      links.push({
        linkText: text.csrd,
        link: csrdRoutePaths.HOME,
        onLinkClick: () => {
          void navigate(csrdRoutePaths.HOME, { state: csrdRestoreLastSubpageState });
        },
        active: 'csrd' === activeLink,
      });
    }

    if (showEnvironment) {
      links.push({
        linkText: text.environment,
        link: '/environment',
        onLinkClick: () => {
          void navigate('/environment');
        },
        active: 'environment' === activeLink,
      });
    }

    if (showLearning && hasLearningMarket) {
      links.push({
        linkText: text.learning,
        link: '/learning',
        onLinkClick: () => {
          void navigate('/learning');
        },
        active: activeLink === 'learning',
      });
    }

    if (userIsAdminForAnyPartner && !mergeMenusFlag) {
      links.push({
        link: getOnboardingUrl(),
        linkText: text.userManagement,
        active: 'usermanagement' === activeLink,
        onLinkClick: () => {
          void navigate(routePathsAdmin.HOME);
        },
      });
    }

    return links;
  }, [
    userServices,
    profile,
    hasLearningMarket,
    showCO2Impact,
    showCSRD,
    showEnvironment,
    showLearning,
    userIsAdminForAnyPartner,
    mergeMenusFlag,
    serviceNames,
    activeLink,
    navigate,
    text,
  ]);

  const hasNavWatermark = document.getElementById('nav-watermark') !== null;

  const buttonIcon = showMenu ? (
    <MenuButtonWrapper>
      <Close color={colors.base.white} width={spacing.small} height={spacing.small} />
    </MenuButtonWrapper>
  ) : (
    <MenuButtonWrapper>
      <Menu color={colors.base.white} width={spacing.small} height={spacing.small} />
    </MenuButtonWrapper>
  );

  return (
    <MobileMenuWrapper>
      <MenuButton
        onClick={() => {
          toggleMenu();
        }}
      >
        {buttonIcon}
      </MenuButton>
      {showMenu && (
        <MobileMenuFoldout hasNavWatermark={hasNavWatermark}>
          <MobileMenuFoldoutList hasNavWatermark={hasNavWatermark}>
            {menuLinks.map((menuLink) => (
              <MobileMenuFoldoutListItem key={menuLink.linkText} data-testid={menuLink.linkText}>
                <MobileMenuLink
                  isActive={menuLink.active}
                  href={menuLink.link}
                  onClick={(e) => {
                    e.preventDefault();
                    menuLink.onLinkClick();
                    toggleMenu();
                  }}
                >
                  {menuLink.linkText}
                </MobileMenuLink>
              </MobileMenuFoldoutListItem>
            ))}
            <MobileMenuFoldoutListItem key="support" data-testid="support">
              <MobileMenuLink
                isActive={activeRoute === '/support'}
                href={routePathsHome.SUPPORT}
                onClick={(e) => {
                  e.preventDefault();
                  void navigate(routePathsHome.SUPPORT);
                  toggleMenu();
                }}
              >
                {text.contactSupport}
              </MobileMenuLink>
            </MobileMenuFoldoutListItem>
          </MobileMenuFoldoutList>
        </MobileMenuFoldout>
      )}
    </MobileMenuWrapper>
  );
};
